import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Eye, Settings2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { BoxArt, SortableWorry, DueCheck, Evidence, fmtTime } from "@/components/worry/WorryParts";

const AddWorry = ({ onAdded }) => {
  const [text, setText] = useState("");
  const [knowOn, setKnowOn] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/worry", { text, know_on: knowOn || null }); setText(""); setKnowOn(""); toast.success("Locked in the box 🔒 You can come back to it at worry time."); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mm-card p-5" data-testid="worry-add">
      <label className="font-semibold" htmlFor="worry-text">What's on your mind?</label>
      <textarea id="worry-text" className="mm-input mt-2 min-h-[90px]" maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your worry in a sentence or two" data-testid="worry-text" />
      <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
        <label className="text-sm">When will I know? <span className="text-mm-muted">(optional)</span>
          <input type="date" className="mm-input !py-1.5 !w-auto mt-1 block" value={knowOn} onChange={(e) => setKnowOn(e.target.value)} data-testid="worry-know-on" />
        </label>
        <button className="mm-btn" disabled={busy || !text.trim()} data-testid="worry-lock-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}Lock it in the box</button>
      </div>
    </form>
  );
};

const WorrySettings = ({ s, onSaved }) => {
  const [time, setTime] = useState(s.time);
  const [notify, setNotify] = useState(s.notify);
  const save = async () => {
    try { await api.put("/worry/settings", { time, notify }); toast.success("Worry time saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mm-card p-5 flex flex-wrap items-end gap-4" data-testid="worry-settings">
      <label className="text-sm">Daily worry time<input type="time" className="mm-input !py-1.5 !w-auto mt-1 block" value={time} onChange={(e) => setTime(e.target.value)} data-testid="worry-settings-time" /></label>
      <label className="text-sm flex items-center gap-2 pb-2"><input type="checkbox" className="w-4 h-4" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="worry-settings-notify" />Phone notification</label>
      <button className="mm-btn !py-2 text-sm" onClick={save} disabled={!time} data-testid="worry-settings-save">Save</button>
      <p className="text-xs text-mm-muted w-full">The box opens for {s.minutes} minutes at this time each day.</p>
    </div>
  );
};

export default function WorryBox() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [peek, setPeek] = useState(params.get("worry_time") === "1");
  const [askPeek, setAskPeek] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(() => api.get("/worry", { params: peek ? { peek: true } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [peek]);
  useEffect(() => { load(); }, [load]);

  const sortW = async (id, action, step) => {
    try { await api.post(`/worry/${id}/sort`, { action, step }); toast.success({ let_go: "Let go 🍃 Watch it float away.", plan: "Step saved", keep: "Kept for tomorrow's worry time" }[action]); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const outcome = async (id, o) => {
    try { await api.post(`/worry/${id}/outcome`, { outcome: o }); toast.success(o === "no" ? "It didn't happen. Noted 💜" : "You got through it 💜"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-mm-primary" /></div>;
  const open = data.is_open || peek;
  const texts = data.boxed.every((w) => w.text !== null);
  return (
    <div className="max-w-3xl space-y-6" data-testid="worry-page">
      <PageHeader back title="Worry Box" subtitle="Put worries aside until your worry time. It's a self-help tool that helps you practise letting thoughts wait." />
      <AddWorry onAdded={load} />
      {data.due_checks.length > 0 && <div className="space-y-2" data-testid="worry-due-checks">{data.due_checks.map((w) => <DueCheck key={w.id} w={w} onOutcome={outcome} />)}</div>}
      <Evidence e={data.evidence} />
      <section className="mm-card p-6 text-center" data-testid="worry-box">
        <BoxArt count={data.boxed.length} open={open && data.boxed.length > 0} />
        <p className="font-semibold mt-4" data-testid="worry-box-status">
          {data.boxed.length ? `${data.boxed.length} worr${data.boxed.length === 1 ? "y" : "ies"} in the box` : "Your box is empty"}
          {" · "}{data.is_open ? `Worry time is open until ${fmtTime(data.closes_at)}` : `Worry time opens at ${fmtTime(data.opens_at)}`}
        </p>
        <div className="flex flex-wrap justify-center gap-2 mt-3">
          {!open && data.boxed.length > 0 && <button className="mm-btn-ghost text-sm" onClick={() => setAskPeek(true)} data-testid="worry-peek-btn"><Eye size={15} />I need to look now</button>}
          <button className="mm-btn-ghost text-sm" onClick={() => setShowSettings((v) => !v)} data-testid="worry-settings-btn"><Settings2 size={15} />Worry time: {data.settings.time}</button>
        </div>
        {askPeek && !peek && (
          <div className="mt-4 rounded-2xl bg-mm-primary/10 p-4 text-sm" data-testid="worry-peek-confirm">
            <p>That's okay. Waiting until worry time teaches your mind that worries can wait. If you can, try waiting a little longer.</p>
            <div className="flex justify-center gap-3 mt-3">
              <button className="mm-btn !py-2 text-sm" onClick={() => setAskPeek(false)} data-testid="worry-peek-wait">I'll wait</button>
              <button className="mm-btn-ghost !py-2 text-sm" onClick={() => setPeek(true)} data-testid="worry-peek-yes">Open it now</button>
            </div>
          </div>
        )}
      </section>
      {showSettings && <WorrySettings s={data.settings} onSaved={() => { setShowSettings(false); load(); }} />}
      {open && texts && data.boxed.length > 0 && (
        <section className="space-y-2" data-testid="worry-open-list">
          <h2 className="font-semibold">For each worry, choose one</h2>
          {data.boxed.map((w) => <SortableWorry key={w.id} w={w} onSort={sortW} />)}
        </section>
      )}
      {data.planned.length > 0 && (
        <section className="mm-card p-5" data-testid="worry-planned">
          <h2 className="font-semibold mb-2">Small steps you planned</h2>
          <ul className="space-y-2 text-sm">{data.planned.map((w) => <li key={w.id} className="break-words"><span className="text-mm-muted">{w.text}</span><br />→ {w.step}</li>)}</ul>
        </section>
      )}
    </div>
  );
}
